import React, { useEffect, useMemo, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { Button, Grid, Typography, CircularProgress } from '@material-ui/core'
import Box from '@material-ui/core/Box'
import { makeStyles, Theme } from '@material-ui/core/styles'
import ResponsiveModal, { ModalSizes } from '@src/components/ResponsiveModal'
import {
  ListKits,
  ListWarning,
  MilliplexKitPrice,
} from '@src/types/graphql-types'
import NonPremixedProduct from './NonPremixedProduct'
import PremixedProduct from './PremixedProduct'
import {
  ActiveLinkType,
  AssayTypeOptions,
  SampleTypeYesNoOptions,
} from '../../enum'
import { useFormikContext } from 'formik'
import { MilliplexFormValues } from '../../Milliplex'
import { parseParams } from '../../Step2/SearchResult/ProductCard/ProductCard'
import { useMilliplexKitAndSampleTypeLazyQuery } from '@src/queries/MilliplexKitAndSampleType.generated'
import { useRouter } from 'next/router'
import {
  getSelectedKitQueryString,
  isPremixedProduct,
} from '@src/routes/Configurators/products/Milliplex/utils'

const useStyles = makeStyles((theme: Theme) => {
  return {
    buttonContainer: {
      display: 'flex',
      gap: theme.spacing(4),
      justifyContent: 'flex-start',
      margin: theme.spacing(6, 6, 4, 0),
      [theme.breakpoints.down('md')]: {
        margin: theme.spacing(4, 6, 4, 0),
      },
      [theme.breakpoints.down('sm')]: {
        width: '100%',
        height: theme.typography.pxToRem(48),
        marginTop: theme.spacing(8),
        marginBottom: theme.spacing(8),
      },
    },
    modalBodyContainerStyle: {
      margin: theme.spacing(6, 8, 3, 8),
      [theme.breakpoints.down('sm')]: {
        margin: theme.spacing(2, 8, 2, 8),
      },
    },
    modalBodyTitleStyle: {
      [theme.breakpoints.down('sm')]: {
        margin: theme.spacing(3, 2, 2, 4),
      },
    },
    panelIdStyle: {
      paddingBottom: theme.spacing(2),
    },

    panelNameStyle: {
      [theme.breakpoints.down('sm')]: {
        margin: theme.spacing(3, 2, 2, 4),
      },
    },

    brandPrimary: {
      backgroundColor: theme.palette.secondary.main,
      color: '#fff',
      '&:hover': {
        backgroundColor: '#402874',
      },
    },

    premixedInfoContainer: {
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'center',
      gap: theme.spacing(2),
    },
  }
})

interface DesignAndPriceModalProps {
  handleClose: () => void
  open: boolean
  milliplexKit: ListKits
}
export interface SelectedDataType {
  analytes?: string[]
  sampleType?: string
  customPremixing?: string
  spaceSaver?: string
  instrumentTypes?: string[]
}

const DesignAndPriceModal: React.FC<DesignAndPriceModalProps> = ({
  handleClose,
  open,
  milliplexKit,
}) => {
  const formik = useFormikContext<MilliplexFormValues>()
  const classes = useStyles()
  const router = useRouter()
  const [selectedAnalytes, setSelectedAnalytes] = useState<string[]>([])
  const [selectInitialAllAnalytes, setInitialSelectAllAnalytes] =
    useState(false)
  const [fetchMilliplexKitAndSampleType, { loading, data: kitSampleTypeData }] =
    useMilliplexKitAndSampleTypeLazyQuery({
      fetchPolicy: 'network-only',
    })

  const previouslyViewedKit =
    router.query.previouslyViewed || formik.values.previouslyViewed

  const kit = formik.values.selectedKits[milliplexKit.panelId]
  const selectedKit = kit
    ? parseParams({
        [milliplexKit.panelId]: kit.url,
      })[0]
    : null

  const [sampleType, setSampleType] = useState<string>(
    selectedKit?.sampleType || ''
  )

  const isPremixedProductValue = isPremixedProduct(milliplexKit)
  const customPremixingInitialValue = isPremixedProductValue
    ? ''
    : selectedKit?.customPremixing || SampleTypeYesNoOptions.No

  const spaceSaverInitialValue = isPremixedProductValue
    ? ''
    : selectedKit?.spaceSaver || SampleTypeYesNoOptions.No

  const [customPremixing, setCustomPremixing] = useState<string>(
    customPremixingInitialValue
  )
  const [spaceSaver, setSpaceSaver] = useState<string>(spaceSaverInitialValue)
  const [selectedInstrument, setSelectedInstrument] = useState(
    selectedKit?.kitInstrument || ''
  )
  const [radioButtonClicked, setRadioButtonClicked] = useState(false)
  const sampleTypeSelectionAvailable =
    (milliplexKit &&
      milliplexKit?.listSampleType &&
      milliplexKit?.listSampleType?.length > 1) ||
    (kitSampleTypeData &&
      kitSampleTypeData?.getMilliplexKitAnalytesAndSampleType?.listSampleType &&
      kitSampleTypeData?.getMilliplexKitAnalytesAndSampleType?.listSampleType
        .length > 1) ||
    false

  const invalidSampleType = sampleTypeSelectionAvailable && !sampleType

  const [enabledAnalytesList, setEnabledAnalytesList] = useState<string[]>(
    milliplexKit.analytes || []
  )
  const [sampleTypeList, setSampleTypeList] = useState<string[]>(
    milliplexKit.listSampleType || []
  )
  const [listWarnings, setListWarnings] = useState<ListWarning[]>([])
  const [priceData, setPriceData] = useState<MilliplexKitPrice>({
    currency: selectedKit ? kit.data.currency : milliplexKit?.currency,
    price: selectedKit ? kit.data.price : milliplexKit?.price,
  })

  const fetchKitDetails = ({
    analytes,
    sampleType: sampleTypeValue,
    customPremixing: customPremixingValue,
    spaceSaver: spaceSaverSelectionValue,
    instrumentTypes: instrumentTypeValue,
  }: SelectedDataType) => {
    fetchMilliplexKitAndSampleType({
      variables: {
        input: {
          allAnalytesSelected: 'false',
          type: milliplexKit.type,
          selectedAnalytes: analytes || selectedAnalytes,
          analytes: milliplexKit.analytes,
          panelId: milliplexKit.panelId,
          sampleType:
            sampleTypeValue || sampleTypeValue === ''
              ? sampleTypeValue
              : sampleType,
          species: milliplexKit.species,
          researchAreas: milliplexKit.researchAreas,
          customPremixSelection: customPremixingValue || customPremixing,
          spaceSaverSelection: spaceSaverSelectionValue || spaceSaver,
          priceCallRequired:
            !isPremixedProductValue && !!(analytes || customPremixingValue),
          appendToMaterialNumber: milliplexKit?.appendToMaterialNumber || '',
          ...((selectedInstrument || instrumentTypeValue) && {
            instrumentTypes: instrumentTypeValue || [selectedInstrument],
            assayTypeSelection: milliplexKit.assayTypeSelection,
            plateType: milliplexKit.plateType,
          }),
        },
      },
    })
  }
  useEffect(() => {
    if (open) {
      if (!selectedKit && previouslyViewedKit === milliplexKit.panelId) {
        if (
          kitSampleTypeData?.getMilliplexKitAnalytesAndSampleType?.listAnalytes
        ) {
          setSelectedAnalytes([])
        }
      } else {
        const checkedAnalytes = selectedKit?.analytes || formik.values.analytes
        if (
          kitSampleTypeData?.getMilliplexKitAnalytesAndSampleType?.listAnalytes
        ) {
          const selectedEnabledAnalytes = checkedAnalytes.filter((single) =>
            kitSampleTypeData?.getMilliplexKitAnalytesAndSampleType?.listAnalytes?.includes(
              single
            )
          )
          setSelectedAnalytes(selectedEnabledAnalytes)
        } else setSelectedAnalytes(checkedAnalytes)
      }
      setSampleType(selectedKit?.sampleType || '')
      if (!isPremixedProductValue) {
        setCustomPremixing(
          selectedKit?.customPremixing || SampleTypeYesNoOptions.No
        )
        setSpaceSaver(selectedKit?.spaceSaver || SampleTypeYesNoOptions.No)
        setSelectedInstrument(selectedKit?.kitInstrument || '')
        fetchKitDetails({
          analytes:
            previouslyViewedKit === milliplexKit.panelId && !selectedKit
              ? milliplexKit?.analytes
              : selectedKit?.analytes || formik.values.analytes,
          sampleType: selectedKit?.sampleType || '',
          customPremixing:
            selectedKit?.customPremixing || SampleTypeYesNoOptions.No,
          spaceSaver: selectedKit?.spaceSaver || SampleTypeYesNoOptions.No,
          ...(selectedInstrument && { instrumentTypes: [selectedInstrument] }),
        })
      }
    }
  }, [open])

  const memoizedKitAndSampleData = useMemo(() => {
    return kitSampleTypeData?.getMilliplexKitAnalytesAndSampleType
  }, [kitSampleTypeData])
  useEffect(() => {
    if (memoizedKitAndSampleData?.listAnalytes) {
      setEnabledAnalytesList(memoizedKitAndSampleData?.listAnalytes)
      //check if any anlytes are selected which is not in the list. If so, unselect those.
      const selectedEnabledAnalytes = selectedAnalytes.filter((single) =>
        memoizedKitAndSampleData?.listAnalytes?.includes(single)
      )

      if (selectedEnabledAnalytes.length !== selectedAnalytes.length) {
        setSelectedAnalytes(selectedEnabledAnalytes)
      }
    }
    if (memoizedKitAndSampleData?.listSampleType) {
      setSampleTypeList(memoizedKitAndSampleData?.listSampleType)
    }
    if (memoizedKitAndSampleData?.listWarnings) {
      setListWarnings(memoizedKitAndSampleData.listWarnings)
    }
    if (memoizedKitAndSampleData?.priceData) {
      setPriceData(memoizedKitAndSampleData?.priceData)
    }
  }, [memoizedKitAndSampleData, selectedInstrument])

  const checkMinimumSelectedAnalytesCount = () => {
    const kitSelectedAnalytes = selectedAnalytes.filter((single) =>
      milliplexKit.analytes.includes(single)
    )
    return (
      milliplexKit.assayType !== 'Premixed Kit' &&
      kitSelectedAnalytes.length < (milliplexKit?.minimumAnalytesCount || 1)
    )
  }
  const handleAnalyteClick = (item: string) => {
    setInitialSelectAllAnalytes(false)
    const itemIsAlreadySelected = selectedAnalytes.includes(item)
    let updatedData: string[] = []
    if (itemIsAlreadySelected) {
      updatedData = selectedAnalytes.filter((analyte) => item !== analyte)
    } else {
      updatedData = [...selectedAnalytes, item]
    }
    setSelectedAnalytes(updatedData)
    fetchKitDetails({ analytes: updatedData })
    setRadioButtonClicked(false)
  }

  const handleSelectAndAddButtonClick = () => {
    const isPremixedKit = milliplexKit.assayType === 'Premixed Kit'
    const quantity = 1

    const kitSelectedAnalytes = selectedAnalytes.filter((single) =>
      milliplexKit.analytes.includes(single)
    )
    let defaultSelectionKit
    if (router.query.activeLink === ActiveLinkType.SelectKits) {
      defaultSelectionKit = router.query.analytes
    } else {
      const kit = formik.values.selectedKits[milliplexKit.panelId]
      const selectedKit = kit
        ? parseParams({
            [milliplexKit.panelId]: kit.url,
          })[0]
        : null
      defaultSelectionKit = selectedKit?.defaultAnalytes?.join(',')
    }
    const newSelectedKits = {
      ...formik.values.selectedKits,
      [milliplexKit?.panelId as string]: {
        url: isPremixedKit
          ? `${milliplexKit.panelId}_${isPremixedKit}__${sampleType}___${quantity}`
          : `${
              milliplexKit.panelId
            }_${isPremixedKit}_${kitSelectedAnalytes.join(
              ','
            )}_${sampleType}_${spaceSaver}_${customPremixing}_${quantity}_${selectedInstrument}_${defaultSelectionKit}`,
        data: {
          productNo: milliplexKit.panelId,
          productName: milliplexKit.panelName,
          price: priceData?.price || '',
          analytesInKit: isPremixedKit
            ? milliplexKit.analytes
            : kitSelectedAnalytes,
          sampleType,
          customPremixing,
          spaceSaving: spaceSaver,
          quantity: quantity,
          currency: priceData.currency || '',
          milliplexKitDataFromApi: milliplexKit,
          kitInstrument: selectedInstrument,
        },
      },
    }

    formik.setFieldValue('selectedKits', newSelectedKits)
    const urlString = getSelectedKitQueryString(newSelectedKits)
    const currentQueryParams = {
      ...router.query,
      selectedKits: urlString,
    }
    router.replace(
      {
        pathname: router.pathname,
        query: currentQueryParams,
      },
      undefined,
      { shallow: true, scroll: false }
    )
    handleClose()
  }

  const handleSampleTypeSelection = (sampleType: string) => {
    setInitialSelectAllAnalytes(false)
    let sampleTypeValue: string = ''
    if (sampleType !== 'Select') {
      sampleTypeValue = sampleType
    }
    setSampleType(sampleTypeValue)
    !isPremixedProductValue && fetchKitDetails({ sampleType: sampleTypeValue })
  }
  useEffect(() => {
    if (
      previouslyViewedKit === milliplexKit.panelId &&
      !formik.values.selectedKits[previouslyViewedKit]
    ) {
      setSelectedAnalytes([])
      setEnabledAnalytesList(milliplexKit.analytes)
      fetchKitDetails({ analytes: [] })
    }
  }, [previouslyViewedKit, router.query.selectedKits, selectedInstrument, open])

  return (
    <ResponsiveModal
      open={open}
      onClose={handleClose}
      renderTitle={() => (
        <Typography
          variant="h2"
          component="h2"
          className={classes.modalBodyTitleStyle}
        >
          <FormattedMessage
            id="MILLIPLEX_DESIGN_AND_PRICE_KIT"
            defaultMessage="DESIGN & PRICE KIT"
          />
        </Typography>
      )}
      size={ModalSizes.Large}
      disableEscapeKeyDown
    >
      <Box className={classes.modalBodyContainerStyle}>
        <Grid container spacing={1}>
          <Typography className={classes.panelIdStyle}>
            {milliplexKit?.panelId}
          </Typography>
        </Grid>
        <Grid container spacing={1}>
          <Typography
            variant="h2"
            component="h2"
            className={classes.panelNameStyle}
          >
            {milliplexKit?.panelName}
          </Typography>
        </Grid>
        {isPremixedProductValue ? (
          <PremixedProduct
            milliplexKit={milliplexKit}
            sampleType={sampleType}
            sampleTypeList={sampleTypeList}
            handleSampleTypeSelection={handleSampleTypeSelection}
            fetchKitDetails={fetchKitDetails}
            enabledAnalytesList={enabledAnalytesList}
          />
        ) : (
          <NonPremixedProduct
            milliplexKit={milliplexKit}
            selectedAnalytes={selectedAnalytes}
            setSelectedAnalytes={setSelectedAnalytes}
            handleAnalyteClick={handleAnalyteClick}
            setCustomPremixing={setCustomPremixing}
            setSpaceSaver={setSpaceSaver}
            customPremixing={customPremixing}
            spaceSaver={spaceSaver}
            sampleType={sampleType}
            enabledAnalytesList={enabledAnalytesList}
            sampleTypeList={sampleTypeList}
            handleSampleTypeSelection={handleSampleTypeSelection}
            listWarnings={listWarnings}
            fetchKitDetails={fetchKitDetails}
            priceData={priceData}
            selectedInstrument={selectedInstrument}
            setSelectedInstrument={setSelectedInstrument}
            selectInitialAllAnalytes={selectInitialAllAnalytes}
            setInitialSelectAllAnalytes={setInitialSelectAllAnalytes}
            loading={loading}
            radioButtonClicked={radioButtonClicked}
            setRadioButtonClicked={setRadioButtonClicked}
          />
        )}
        <Grid container className={classes.buttonContainer}>
          <Button
            className={classes.brandPrimary}
            variant="contained"
            size="large"
            onClick={handleSelectAndAddButtonClick}
            disabled={
              loading ||
              checkMinimumSelectedAnalytesCount() ||
              invalidSampleType ||
              (!isPremixedProductValue &&
                !selectedInstrument &&
                previouslyViewedKit === milliplexKit.panelId &&
                milliplexKit.assayTypeSelection === AssayTypeOptions.Screening)
            }
          >
            <FormattedMessage
              id="MILLIPLEX_SELECT_AND_ADD"
              defaultMessage="Select & Add"
            />
            {loading && (
              <>
                &nbsp;
                <CircularProgress color="inherit" size={20} thickness={5} />
              </>
            )}
          </Button>
        </Grid>
      </Box>
    </ResponsiveModal>
  )
}
export default DesignAndPriceModal
